import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { firstValueFrom, of } from 'rxjs';
import { catchError, concatMap, map, tap, filter } from 'rxjs/operators';

import { LiveData } from '../../models/api';
import { LiveService } from '../../services/live.service';
import { LocalStorageService } from '../../services/local-storage.service';
import * as HomeActions from './home.actions';
import * as fromHome from './home.reducer';
import {
  selectExternalLives,
  selectUIState,
  selectLives,
  selectIsShowingButtons,
} from './home.selectors';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { Cas } from '../../models/home';

@Injectable()
export class HomeEffects {
  loadLives$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HomeActions.loadLives),
      concatMap(() =>
        this.liveService.lives$.pipe(
          map(
            ({
              lives,
              connectionCount,
              cas,
            }: {
              lives: LiveData[];
              connectionCount: number;
              cas: Cas[];
            }) =>
              HomeActions.loadLivesSuccess({
                livesRaw: lives ?? [],
                connectionCount,
                cas,
              }),
          ),
          // tap(console.log),
          catchError((error) => of(HomeActions.loadLivesFailure({ error }))),
        ),
      ),
    );
  });

  storeSetting$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          HomeActions.toggleFilter,
          HomeActions.addExternalLive,
          HomeActions.removeExternalLive,
          HomeActions.toggleButtons,
        ),
        tap(async () => {
          const ui = await firstValueFrom(
            this.store.pipe(select(selectUIState)),
          );
          const externalLives = await firstValueFrom(
            this.store.pipe(select(selectExternalLives)),
          );
          const isShowingButtons = await firstValueFrom(
            this.store.pipe(select(selectIsShowingButtons)),
          );
          this.localStorageService.updateHomeSetting({
            filter: ui.filter,
            playerGridCol: 2,
            externalLives: externalLives.map((l) => l.videoId),
            isShowingButtons,
          });
        }),
      );
    },
    { dispatch: false },
  );

  sendVideoIdToAnalytics = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(HomeActions.turnOn),
        concatMap(({ videoId }) =>
          this.store
            .select(selectLives)
            .pipe(map((lives) => lives.find((l) => l.videoId === videoId))),
        ),
        filter((live) => !!live),
        map((live) => ({
          videoId: live?.videoId,
          author: live?.author,
          title: live?.title,
        })),
        // tap(console.log),
        tap((live) => {
          logEvent(this.anal, 'turnOn', live);
        }),
      );
    },
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private liveService: LiveService,
    private store: Store<fromHome.State>,
    private localStorageService: LocalStorageService,
  ) {}

  private anal = inject(Analytics);

  ngrxOnInitEffects() {
    return HomeActions.loadLives();
  }
}
